import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ShieldCheck, Send, Clock, User, Mail, CheckCircle2, MessageCircle, Filter } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

/**
 * Admin-only inmail viewer.
 * Route: /dashboard/admin-inmail (mounted inside Dashboard).
 * Only accessible when the current user is admin (Dashboard router
 * already restricts this route with is_admin gating).
 *
 * Supports deep-linking via `?source=` query param so the widget tiles and
 * awaiting-review page can jump straight to a pre-filtered category.
 */
const AdminInmailViewer = ({ token }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  // Source filter — slice the inbox by application category so ops can
  // triage 32+ items by type. Values map to admin_inmail.source in Mongo:
  //   'all'                 — no filter
  //   'partnership_inquiry' — Partner Applications
  //   'idol_application'    — Idol Submissions
  //   'corporate_signup'    — Sponsors
  //   'support_message'     — General support
  const [sourceFilter, setSourceFilter] = useState(searchParams.get("source") || "all");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (sourceFilter !== "all") params.set("source", sourceFilter);
      const qs = params.toString();
      const url = qs ? `${API}/inmail/admin/inbox?${qs}` : `${API}/inmail/admin/inbox`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data || []);
    } catch (e) {
      toast.error("Failed to load admin in-mail");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter, sourceFilter]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/inmail/admin/${id}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      load();
    } catch { /* noop */ }
  };

  const sendReply = async (id) => {
    const t = replyText.trim();
    if (!t) return;
    setSending(true);
    try {
      await axios.post(`${API}/inmail/admin/${id}/reply`, { text: t }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Reply sent — user notified in-app.");
      setReplyingTo(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-6" data-testid="admin-inmail-viewer">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#EC008C]/15 flex items-center justify-center flex-shrink-0">
            <Mail className="w-6 h-6 text-[#EC008C]" />
          </div>
          <div>
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-1">Admin In-Mail</h1>
            <p className="text-[#8A92A0] text-sm max-w-2xl">
              Private, secure, in-house messages from users. Never routed through external email.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-[#8A92A0]" />
          {[
            { key: "all", label: "All" },
            { key: "unread", label: "Unread" },
            { key: "read", label: "Read" },
            { key: "replied", label: "Replied" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === f.key
                  ? "bg-[#EC008C] text-black"
                  : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Source (category) filter — Partner / Idol / Corporate / Support */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="source-filter-row">
        <span className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Category:</span>
        {[
          { key: "all", label: "All" },
          { key: "partnership_inquiry", label: "Partner Applications" },
          { key: "idol_application", label: "Idol Submissions" },
          { key: "corporate_signup", label: "Sponsors" },
          { key: "sponsor_contribution", label: "Contributions" },
          { key: "support_message", label: "Support" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => {
              setSourceFilter(f.key);
              // Sync URL so deep-links from the Admin Inbox widget survive refresh.
              if (f.key === "all") {
                searchParams.delete("source");
              } else {
                searchParams.set("source", f.key);
              }
              setSearchParams(searchParams, { replace: true });
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              sourceFilter === f.key
                ? "bg-[#F0C464] text-black"
                : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
            }`}
            data-testid={`source-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card p-12 text-center text-[#8A92A0]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center" data-testid="admin-inmail-empty">
          <MessageCircle className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-2">Inbox zero</h3>
          <p className="text-[#8A92A0] text-sm">
            {statusFilter === "unread" ? "No unread in-mail. Nice work." : "No in-mail here."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => (
            <div
              key={m.inmail_id}
              className={`card p-5 ${m.status === "unread" ? "border-[#EC008C]/40 bg-[#EC008C]/5" : ""}`}
              data-testid={`inmail-row-${m.inmail_id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-[#1A2030] flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-[#8A92A0]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold text-sm">{m.from_name}</span>
                    <span className="text-xs text-[#4A5260]">{m.from_email}</span>
                    <span className="text-[10px] uppercase tracking-wide bg-[#0A0E18] border border-[#1A2030] rounded px-1.5 py-0.5 text-[#8A92A0]">
                      {m.from_role || "user"}
                    </span>
                    {m.status === "unread" && (
                      <span className="text-[10px] uppercase tracking-wide bg-[#EC008C] text-black rounded px-1.5 py-0.5 font-bold">NEW</span>
                    )}
                    {m.status === "replied" && (
                      <span className="text-[10px] uppercase tracking-wide bg-[#8DB87A]/20 text-[#8DB87A] rounded px-1.5 py-0.5 font-bold flex items-center gap-0.5">
                        <CheckCircle2 className="w-3 h-3" /> replied
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-sm text-white font-medium">{m.subject}</p>
                    <span className="text-xs text-[#4A5260] flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmt(m.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-[#C0C6D0] whitespace-pre-wrap break-words pl-12">{m.text}</p>

              {m.admin_reply && (
                <div className="mt-3 ml-12 p-3 rounded-xl bg-[#EC008C]/5 border border-[#EC008C]/20">
                  <p className="text-xs text-[#EC008C] mb-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> Your reply · {fmt(m.admin_replied_at)}
                  </p>
                  <p className="text-sm text-white whitespace-pre-wrap break-words">{m.admin_reply}</p>
                </div>
              )}

              <div className="flex items-center gap-2 mt-4 pl-12">
                {m.status === "unread" && (
                  <Button
                    onClick={() => markRead(m.inmail_id)}
                    variant="outline"
                    className="border-[#1A2030] text-white hover:border-[#EC008C] rounded-full text-xs h-8"
                    data-testid={`mark-read-${m.inmail_id}`}
                  >
                    Mark as read
                  </Button>
                )}
                {!m.admin_reply && (
                  <Button
                    onClick={() => { setReplyingTo(m.inmail_id); setReplyText(""); }}
                    className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full text-xs h-8 px-4"
                    data-testid={`reply-btn-${m.inmail_id}`}
                  >
                    <Send className="w-3 h-3 mr-1" /> Reply
                  </Button>
                )}
              </div>

              {replyingTo === m.inmail_id && (
                <div className="mt-3 pl-12 space-y-2" data-testid={`reply-form-${m.inmail_id}`}>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply — the user will get an in-app notification."
                    rows={3}
                    maxLength={4000}
                    className="bg-[#050810] border-[#1A2030] text-white resize-none"
                    data-testid={`reply-textarea-${m.inmail_id}`}
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => sendReply(m.inmail_id)}
                      disabled={sending || !replyText.trim()}
                      className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-full text-xs h-8 px-4"
                      data-testid={`send-reply-${m.inmail_id}`}
                    >
                      {sending ? "Sending..." : <><Send className="w-3 h-3 mr-1" /> Send Reply</>}
                    </Button>
                    <Button
                      onClick={() => { setReplyingTo(null); setReplyText(""); }}
                      variant="outline"
                      className="border-[#1A2030] text-white hover:border-[#D14A85] rounded-full text-xs h-8"
                      data-testid={`cancel-reply-${m.inmail_id}`}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminInmailViewer;
